import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DefaultPalette } from 'office-ui-fabric-react';
import { t } from 'i18next';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { buttonStylesForProjects, titleCommandBarButtonStyles } from '../../styles/button-styles';
import { NodoOrganigramaDataGrid } from '../../system/organization_chart/nodo-organigrama-datagrid.interface';
import DataEditor, { GridCell, GridCellKind, GridColumn, Item, EditableGridCell, DataEditorRef } from '@glideapps/glide-data-grid';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { User } from '../../system/User';
import Loading from '../Spinners/Loading';
import { OrganizationChartLevel } from '../../system/organization_chart/OrganizationChartLevel';
import { OPERATORS, TYPES } from '../../common/list-utils';
import { CommandBarButton, IComboBoxOption, Icon, IIconProps, IStackStyles, Text, TooltipHost } from '@fluentui/react';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import '@glideapps/glide-data-grid/dist/index.css';
import { handleColumnResizeUtil } from '../../lib/GlideDataGrid/UtilsGlideGrid';
import ComboBoxSearch from '../Common/ComboBoxSearch';
import { DetailListPagination } from '../Common/DetailsList/DetailListPagination';
import { normalizeEmails } from '../../common/normalizeEmails';

export interface INotificationByHierarchies {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
  path: string;
}

type NodoOrganigramaRow = NodoOrganigramaDataGrid['data'][number];
// El backend tipa `nors_id` como `null`, pero tras un upsert devuelve un id real.
type NotificationRow = Omit<NodoOrganigramaRow, 'nors_id'> & { nors_id: string | null };

const NOTIFICATION_COLUMN_ID = 'nors_mails';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const iconClass = mergeStyles({
  position: 'relative',
  top: 2,
  left: 4,
  cursor: 'pointer',
});

const stackStyles: Partial<IStackStyles> = { root: { height: 32, fontSize: 12 } };
const FilterClearIcon: IIconProps = { iconName: 'ClearFilter' };

export default function NotificationByHierarchies(props: INotificationByHierarchies) {
  const [data, setData] = useState<NotificationRow[]>([]);
  const [originalData, setOriginalData] = useState<NotificationRow[]>([]);
  const [listLevelsFilters, setListLevelsFilters] = useState<
    {
      quick: boolean;
      name: string;
      type: TYPES;
      placeholder: string;
      operator: OPERATORS;
      contextForCombobox: IComboBoxOption[];
    }[]
  >([]);
  const [filters, setFilters] = useState<{ [index: string]: { key: string; value: string; formatVal: string; placeholder: string } }>({});
  const [levels, setLevels] = useState<OrganizationChartLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const editorRef = useRef<DataEditorRef | null>(null);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- CARGA DE NIVELES ---
  useEffect(() => {
    (async () => {
      props.changeBlockUI(true);
      setLoading(true);
      const levelsResponse = await props.system.getOrganizationChartSystem().getLevels();
      const formattedLevels = await Promise.all(
        levelsResponse.map(async (item) => {
          const organigramaData = await props.system.getOrganizationChartSystem().getLastLevelNodesss(item.getLevel().toString());
          return {
            quick: true,
            name: item.getNameTec(),
            type: TYPES.COMBOBOXTEXT,
            placeholder: t(item.getName()),
            operator: OPERATORS.CONTAINS,
            contextForCombobox: organigramaData.levels,
          };
        }),
      );
      setLevels(levelsResponse);
      setListLevelsFilters(formattedLevels);

      await fetchData();

      props.changeBlockUI(false);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (Object.keys(listLevelsFilters).length > 0) {
      (async () => {
        props.changeBlockUI(true);
        await fetchData();
        props.changeBlockUI(false);
      })();
    }
  }, [filters]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, currentPage, pageSize]);

  const totalPages = useMemo(() => Math.ceil(data.length / pageSize), [data.length, pageSize]);

  // --- CARGA DE DATOS ---
  const fetchData = async () => {
    try {
      const orgSystem = props.system.getOrganizationChartSystem();
      const activeFilters = Object.entries(filters).map(([key, { value }]) => {
        const def = listLevelsFilters.find((f) => f.name === key);
        return {
          name: key,
          value: value,
          operator: def?.operator || 'contains',
        };
      });

      const dataGrid = await orgSystem.getDataGrid(1000, activeFilters);
      setData(dataGrid.data);
      setOriginalData(JSON.parse(JSON.stringify(dataGrid.data)));
      setCurrentPage(1);
    } catch (error) {
      console.error({ error });
    }
  };

  // --- COLUMNAS ---
  useEffect(() => {
    const dynamicColumns: GridColumn[] = levels.map((lvl) => ({
      title: t(lvl.getName()),
      id: lvl.getNameTec(),
      themeOverride: {
        headerFontStyle: '900 14px',
      },
      grow: 1,
    }));

    const notificationColumn: GridColumn = {
      id: NOTIFICATION_COLUMN_ID,
      title: t('Notificación directa'),
      themeOverride: { bgHeaderHovered: '#f4f9ff', headerFontStyle: '900 14px' },
      grow: 2,
      width: 280,
    };

    setColumns([...dynamicColumns, notificationColumn]);
  }, [levels]);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const column = columns[col];
      const item = paginatedData[row];

      if (!item || !column) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
          readonly: true,
        };
      }

      if (column.id === NOTIFICATION_COLUMN_ID) {
        const value = item.nors_mails ?? '';
        return {
          kind: GridCellKind.Text,
          data: value,
          displayData: value,
          allowOverlay: true,
          readonly: false,
        };
      }

      const value = item[column.id as keyof typeof item];
      return {
        kind: GridCellKind.Text,
        data: String(value ?? ''),
        displayData: String(value ?? ''),
        allowOverlay: false,
        readonly: true,
      };
    },
    [paginatedData, columns],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const column = columns[col];

      if (!column || column.id !== NOTIFICATION_COLUMN_ID || row >= paginatedData.length || newValue.kind !== GridCellKind.Text) return;

      const realRowIndex = (currentPage - 1) * pageSize + row;
      const copy = [...data];
      copy[realRowIndex] = { ...copy[realRowIndex], nors_mails: String(newValue.data) };

      setData(copy);
    },
    [columns, data, paginatedData, currentPage, pageSize],
  );

  const handleColumnResize = useCallback(
    handleColumnResizeUtil((cols) => setColumns(cols)),
    [],
  );

  const changedRows = useMemo(
    () =>
      data
        .map((current, index) => ({ current, original: originalData[index], index }))
        .filter(({ current, original }) => original && (current.nors_mails ?? '').trim() !== (original.nors_mails ?? '').trim()),
    [data, originalData],
  );

  const hasChanges = changedRows.length > 0;

  // --- GUARDAR ---
  const handleSave = useCallback(async () => {
    if (changedRows.length === 0) {
      props.courier.messageInfo(t('No hay cambios para guardar'));
      return;
    }

    for (const { current } of changedRows) {
      const mails = (current.nors_mails ?? '').trim();
      if (!mails) continue;

      const emails = mails.split(',');
      const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email.trim()));

      if (invalidEmails.length > 0) {
        props.courier.messageError(
          `${t(invalidEmails.length > 1 ? 'Algunos de los correos que has ingresado son inválidos' : 'Un correo que has ingresado es inválido')} (${current.nor_nombre})`,
        );
        return;
      }
    }

    props.changeBlockUI(true);

    try {
      const ctrl = props.system.getNodoOrganigramaSeguimientoSystem();
      const results = await Promise.allSettled(
        changedRows.map(async ({ current, index }) => {
          const cleanEmails = normalizeEmails(current.nors_mails ?? '');
          const result = await ctrl.upsert({
            nors_id: current.nors_id as any,
            nors_norid: current.nor_id as any,
            nors_mails: cleanEmails,
          });
          return { index, result };
        }),
      );

      const updatedData = [...data];
      const updatedOriginal = [...originalData];
      let successCount = 0;
      let errorCount = 0;

      results.forEach((settled) => {
        if (settled.status === 'fulfilled') {
          const { index, result } = settled.value;
          updatedData[index] = { ...updatedData[index], nors_mails: result.data.nors_mails, nors_id: result.data.nors_id };
          updatedOriginal[index] = { ...updatedData[index] };
          successCount += 1;
        } else {
          errorCount += 1;
        }
      });

      setData(updatedData);
      setOriginalData(updatedOriginal);

      if (errorCount === 0) {
        props.courier.messageSuccess(t(successCount > 1 ? 'Correos electrónicos guardados correctamente' : 'Correo electrónico guardado correctamente'));
      } else if (successCount > 0) {
        props.courier.messageError(t('Algunos registros no se pudieron guardar, intente nuevamente'));
      } else {
        props.courier.messageError(t('Error al guardar los cambios'));
      }
    } catch (error) {
      console.error(error);
      props.courier.messageError(t('Error al guardar los cambios'));
    } finally {
      props.changeBlockUI(false);
    }
  }, [changedRows, data, originalData]);

  // --- MENÚ ---
  const itemsMenu = useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Notificaciónes por área'),
        iconProps: { iconName: 'MailOptions', color: DefaultPalette.themeLighterAlt },
        buttonStyles: titleCommandBarButtonStyles,
      },
      {
        key: 'save',
        text: t('Guardar'),
        iconProps: { iconName: 'Save', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesForProjects,
        onClick: () => void handleSave(),
        disabled: loading || !hasChanges,
      },
    ],
    [handleSave, loading, hasChanges],
  );

  // --- ELIMINAR FILTRO DESDE CHIP ---
  const removeFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const getOptionByValues = (opt: any, context: any): string[] => {
    if (!opt || !context) return [''];
    const found = context.find((o: any) => o.key === opt.key && o.text === opt.value);
    return found ? [found.key] : [''];
  };

  if (loading) {
    return <Loading title="Cargando niveles..." description="Por favor, espere un momento, estamos obteniendo los niveles de cada area de organigrama." />;
  }

  return (
    <ViewWrapperWithCommandBar title="" items={itemsMenu} withBorderBottom>
      <div className="padding-list">
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row row-flex-calendar">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl2 ms-xxl2 filter-menu" dir="ltr">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 0 8px 0' }}>
                <Text block variant="medium" className="ms-hiddenXlUp">
                  {t('Filtros')}
                </Text>

                <CommandBarButton
                  onClick={clearFilters}
                  className="bg-transparent"
                  allowDisabledFocus
                  styles={stackStyles}
                  iconProps={FilterClearIcon}
                  text={t('Limpiar')}
                />
              </div>

              {/* FILTROS */}
              {listLevelsFilters.length > 0 && (
                <div className="ms-Grid-row" dir="ltr">
                  {listLevelsFilters.map((filterConfig) => (
                    <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12" key={filterConfig.name}>
                      <label className="ms-Label label-filter">{filterConfig.placeholder}</label>
                      <ComboBoxSearch
                        options={filterConfig.contextForCombobox.map((opt) => ({ ...opt, title: opt.text }))}
                        className="combo-box-filter-detaillist"
                        selected={getOptionByValues(filters[filterConfig.name], filterConfig.contextForCombobox)}
                        onSelectOption={(option) => {
                          const newFilters = { ...filters };
                          if (option)
                            newFilters[filterConfig.name] = {
                              key: option.key.toString(),
                              value: option.text,
                              formatVal: option.text,
                              placeholder: filterConfig.placeholder,
                            };
                          else delete newFilters[filterConfig.name];
                          setFilters(newFilters);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl10 ms-xxl10 container-data-grid-cost-center"
              style={{ height: '70vh', width: '100%' }}
            >
              <Text block variant="large" className="text-bold title-filter-wrapper mb-2">
                <span className="notification-email-header">
                  {t('Áreas ')}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: "10px"}}>
                  <Icon iconName="Info" className="notification-email-icon" style={{ fontSize: 16 }} />
                  <small style={{ fontSize: "12px"}}>{t('Si se ingresa más de 1 correo separar por una coma (,)')}</small>
                </div>

                {/* TAGS DE FILTROS APLICADOS */}
                {Object.entries(filters).map(([key, { formatVal, placeholder }]) => (
                  <div key={key} className="chip-filter">
                    <label className="chip-filter-label">
                      {placeholder}: {formatVal}
                      <Icon iconName="Cancel" className={iconClass} onClick={() => removeFilter(key)} />
                    </label>
                  </div>
                ))}
              </Text>

              {data.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 50, gap: 20 }}>
                  <Icon iconName="Search" styles={{ root: { fontSize: 38, color: '#ccc' } }} />
                  <Text variant="mediumPlus" styles={{ root: { color: '#666' } }}>
                    {t('No se encontraron resultados')}
                  </Text>
                </div>
              ) : (
                <>
                  <DataEditor
                    width="100%"
                    ref={editorRef}
                    columns={columns}
                    maxColumnWidth={1000}
                    height={'70vh'}
                    getCellContent={getCellContent}
                    onCellEdited={onCellEdited}
                    rows={paginatedData.length}
                    rowMarkers="none"
                    smoothScrollX
                    smoothScrollY
                    rowHeight={35}
                    onColumnResize={handleColumnResize}
                    keybindings={{
                      activateCell: false,
                      copy: false,
                      paste: false,
                      delete: false,
                      selectAll: false,
                    }}
                    editOnType={true}
                  />

                  <div className="mb-2">
                    <DetailListPagination
                      page={currentPage}
                      perPage={pageSize}
                      totalRows={data.length}
                      onChangePerPage={(e, item) => {
                        setPageSize(Number(item.key));
                        setCurrentPage(1);
                      }}
                      onChangePage={(action) => {
                        switch (action) {
                          case 'toFirst':
                            setCurrentPage(1);
                            break;

                          case 'back':
                            setCurrentPage((prev) => Math.max(prev - 1, 1));
                            break;

                          case 'next':
                            setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                            break;

                          case 'toLast':
                            setCurrentPage(totalPages);
                            break;
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
}
